import React from 'react'
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Pagination,
  Stack,
  Chip
} from '@mui/material'
import { poolwerxColors } from '../../theme/colors'

export interface TableColumn<T = any> {
  /** Unique identifier for the column */
  key: string
  /** Column header label */
  label: string
  /** Whether this column is sortable */
  sortable?: boolean
  /** Column alignment */
  align?: 'left' | 'center' | 'right'
  /** Custom width */
  width?: string | number
  /** Custom render function for cell content */
  render?: (row: T, index: number) => React.ReactNode
  /** Custom render function for the column header */
  renderHeader?: () => React.ReactNode
  /** Whether to show this column on mobile */
  hideOnMobile?: boolean
}

export interface PaginationConfig {
  /** Current page (1-based) */
  page: number
  /** Items per page */
  perPage: number
  /** Total number of items */
  total: number
  /** Called when page changes */
  onPageChange: (page: number) => void
  /** Show first/last buttons */
  showFirstLast?: boolean
  /** Pagination size */
  size?: 'small' | 'medium' | 'large'
}

interface BaseDataTableProps<T = any> {
  /** Table columns configuration */
  columns: TableColumn<T>[]
  /** Table data */
  data: T[]
  /** Loading state */
  loading?: boolean
  /** Error message */
  error?: string | null
  /** Empty state message */
  emptyMessage?: string
  /** Current sort field */
  sortField?: string
  /** Current sort direction */
  sortDirection?: 'asc' | 'desc'
  /** Called when sort changes */
  onSort?: (field: string, direction: 'asc' | 'desc') => void
  /** Pagination configuration */
  pagination?: PaginationConfig
  /** Table title */
  title?: string
  /** Icon to display before the title (enhanced variant only) */
  titleIcon?: React.ReactNode
  /**
   * Content for the right of the toolbar header (enhanced variant). For a top
   * results/pagination toolbar, pass the shared `TableResultsToolbar`
   * (`components/shared/TableResultsToolbar`) here - it renders
   * "Showing X-Y of N" + a per-page select + a prev/next pager. Pattern in use:
   * `IntegrationEntityTable`.
   */
  headerActions?: React.ReactNode
  /** Table size */
  size?: 'small' | 'medium'
  /** Custom styling */
  sx?: object
  /** Custom empty state component */
  emptyComponent?: React.ReactNode
  /** Show row hover effect */
  hover?: boolean
  /** Custom row key extractor */
  getRowKey?: (row: T, index: number) => string | number
  /** Custom row styling function */
  getRowSx?: (row: T, index: number) => object
  /** Called when a row is clicked - applies to the whole TableRow */
  onRowClick?: (row: T, index: number) => void
  /** Table variant - 'enhanced' enables toolbar header and teal table headers */
  variant?: 'default' | 'enhanced'
  /** Total count for enhanced count chip (defaults to data.length) */
  totalCount?: number
  /** Label for count chip (e.g., "syncs", "items") */
  countLabel?: string
  /** Show the count chip in enhanced variant */
  showCount?: boolean
}

function BaseDataTable<T = any>({
  columns,
  data,
  loading = false,
  error = null,
  emptyMessage = 'No data available',
  sortField,
  sortDirection = 'asc',
  onSort,
  pagination,
  title,
  titleIcon,
  headerActions,
  size = 'small',
  sx = {},
  emptyComponent,
  hover = true,
  getRowKey = (row: any, index: number) => (row.id ?? index),
  getRowSx,
  onRowClick,
  variant = 'default',
  totalCount,
  countLabel = 'items',
  showCount = false
}: BaseDataTableProps<T>) {
  const isEnhanced = variant === 'enhanced'
  const displayCount = totalCount ?? (pagination?.total ?? data.length)
  
  const handleSort = (field: string) => {
    if (!onSort) return
    
    if (sortField === field) {
      // Toggle direction
      onSort(field, sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      onSort(field, 'asc')
    }
  }

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.perPage) : 0

  if (loading) {
    const loadingContent = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
    return isEnhanced ? (
      <Paper sx={{ overflow: 'hidden', borderRadius: 2, ...sx }}>{loadingContent}</Paper>
    ) : (
      <Card sx={sx}><CardContent>{loadingContent}</CardContent></Card>
    )
  }

  if (error) {
    const errorContent = (
      <Alert severity="error" sx={{ m: isEnhanced ? 2 : 0, mb: 2 }}>
        {error}
      </Alert>
    )
    return isEnhanced ? (
      <Paper sx={{ overflow: 'hidden', borderRadius: 2, ...sx }}>{errorContent}</Paper>
    ) : (
      <Card sx={sx}><CardContent>{errorContent}</CardContent></Card>
    )
  }

  const enhancedHeaderSx = {
    backgroundColor: poolwerxColors.teal,
    borderBottom: `1px solid ${poolwerxColors.tealHover}`,
    color: 'white',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '0.75rem',
    letterSpacing: '0.5px'
  }

  const renderTableHeader = () => {
    // TableCell's align prop only affects inline content; TableSortLabel is inline-flex
    // and needs an explicit centered wrapper when we want the whole header visually centered.
    const wrapAligned = (content: React.ReactNode, align?: string) => {
      if (align !== 'center' && align !== 'right') return content
      const justify = align === 'center' ? 'center' : 'flex-end'
      return <Box sx={{ display: 'flex', justifyContent: justify, alignItems: 'center', width: '100%' }}>{content}</Box>
    }

    if (!isEnhanced) {
      return columns.map((column) => (
        <TableCell
          key={column.key}
          align={column.align || 'left'}
          sx={{
            width: column.width,
            display: column.hideOnMobile ? { xs: 'none', md: 'table-cell' } : 'table-cell'
          }}
        >
          {column.renderHeader ? column.renderHeader() : wrapAligned(
            column.sortable ? (
              <TableSortLabel
                active={sortField === column.key}
                direction={sortField === column.key ? sortDirection : 'asc'}
                onClick={() => handleSort(column.key)}
              >
                <strong>{column.label}</strong>
              </TableSortLabel>
            ) : (
              <strong>{column.label}</strong>
            ),
            column.align
          )}
        </TableCell>
      ))
    }

    return columns.map((column) => (
      <TableCell
        key={column.key}
        align={column.align || 'left'}
        sx={{
          width: column.width,
          display: column.hideOnMobile ? { xs: 'none', md: 'table-cell' } : 'table-cell',
          ...enhancedHeaderSx
        }}
      >
        {column.renderHeader ? column.renderHeader() : wrapAligned(
          column.sortable ? (
            <TableSortLabel
              active={sortField === column.key}
              direction={sortField === column.key ? sortDirection : 'asc'}
              onClick={() => handleSort(column.key)}
              sx={{
                color: 'white !important',
                '&.Mui-active': { color: 'white !important' },
                '& .MuiTableSortLabel-icon': { color: 'white !important' }
              }}
            >
              {column.label}
            </TableSortLabel>
          ) : (
            column.label
          ),
          column.align
        )}
      </TableCell>
    ))
  }

  const renderToolbar = () => {
    if (isEnhanced && (title || titleIcon || showCount || headerActions)) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 2,
          borderBottom: '1px solid #E6E8EA',
          backgroundColor: '#FFFFFF',
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {titleIcon && (
              <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                {titleIcon}
              </Box>
            )}
            {title && (
              <Typography variant="h6" sx={{ mr: 1 }}>
                {title}
              </Typography>
            )}
            {showCount && (
              <Chip
                label={`${displayCount.toLocaleString()} ${displayCount === 1 ? countLabel.replace(/s$/, '') : countLabel}`}
                size="small"
                sx={{
                  backgroundColor: '#E6F4F5',
                  border: '1px solid #00B2CA',
                  color: '#1F2A2E',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: 24
                }}
              />
            )}
          </Box>
          {headerActions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 2 }}>
              {headerActions}
            </Box>
          )}
        </Box>
      )
    }

    if (!isEnhanced && (title || headerActions || pagination)) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {title && (
            <Typography variant="h6">
              {title}
            </Typography>
          )}
          {headerActions}
          {pagination && (
            <Typography variant="body2" color="text.secondary">
              Showing {data.length === 0 ? 0 : (pagination.page - 1) * pagination.perPage + 1}-{Math.min(pagination.page * pagination.perPage, pagination.total)} of {pagination.total} results
            </Typography>
          )}
        </Box>
      )
    }

    return null
  }

  if (isEnhanced) {
    return (
      <Paper sx={{ overflow: 'hidden', borderRadius: 2, ...sx }}>
        {renderToolbar()}
        {data.length === 0 && !loading ? (
          emptyComponent || (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {emptyMessage}
              </Typography>
            </Box>
          )
        ) : (
          <>
            <TableContainer>
              <Table size={size}>
                <TableHead>
                  <TableRow>
                    {renderTableHeader()}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow
                      key={getRowKey(row, index)}
                      hover={hover}
                      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                      sx={{
                        backgroundColor: '#FFFFFF',
                        '&:hover': { backgroundColor: '#E6F4F5' },
                        '& td': { borderBottom: '1px solid #E6E8EA' },
                        ...(onRowClick ? { cursor: 'pointer' } : {}),
                        ...(getRowSx ? getRowSx(row, index) : {})
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={`${getRowKey(row, index)}-${column.key}`}
                          align={column.align || 'left'}
                          sx={{
                            display: column.hideOnMobile ? { xs: 'none', md: 'table-cell' } : 'table-cell'
                          }}
                        >
                          {column.render ? column.render(row, index) : (row as any)[column.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && totalPages > 1 && (
              <Box sx={{ borderTop: '1px solid #E6E8EA', backgroundColor: '#FAFAFA' }}>
                <Stack spacing={2} sx={{ py: 2, alignItems: 'center' }}>
                  <Pagination
                    count={totalPages}
                    page={pagination.page}
                    onChange={(_, newPage) => pagination.onPageChange(newPage)}
                    color="primary"
                    size={pagination.size || 'large'}
                    showFirstButton={pagination.showFirstLast}
                    showLastButton={pagination.showFirstLast}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {pagination.total} {pagination.total !== 1 ? countLabel : countLabel.replace(/s$/, '')} total
                  </Typography>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Paper>
    )
  }

  return (
    <Card sx={sx}>
      <CardContent>
        {renderToolbar()}

        {data.length === 0 && !loading ? (
          emptyComponent || (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {emptyMessage}
              </Typography>
            </Box>
          )
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size={size}>
                <TableHead>
                  <TableRow>
                    {renderTableHeader()}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow
                      key={getRowKey(row, index)}
                      hover={hover}
                      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                      sx={{
                        ...(hover ? {
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            '& .MuiTableCell-root': {
                              borderBottomColor: 'rgba(0, 0, 0, 0.08)'
                            }
                          },
                          transition: 'background-color 0.2s ease-in-out'
                        } : {}),
                        ...(onRowClick ? { cursor: 'pointer' } : {}),
                        ...(getRowSx ? getRowSx(row, index) : {})
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={`${getRowKey(row, index)}-${column.key}`}
                          align={column.align || 'left'}
                          sx={{
                            display: column.hideOnMobile ? { xs: 'none', md: 'table-cell' } : 'table-cell'
                          }}
                        >
                          {column.render ? column.render(row, index) : (row as any)[column.key]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {pagination && totalPages > 1 && (
              <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={pagination.page}
                  onChange={(_, newPage) => pagination.onPageChange(newPage)}
                  color="primary"
                  size={pagination.size || 'large'}
                  showFirstButton={pagination.showFirstLast}
                  showLastButton={pagination.showFirstLast}
                />
                <Typography variant="body2" color="text.secondary">
                  {pagination.total} item{pagination.total !== 1 ? 's' : ''} total
                </Typography>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default BaseDataTable